import { VillageScene } from "./scenes/Village.js";
import { MapCombat01Scene } from "./scenes/MapCombat01.js";
import { clearSavedResume, loadWorldStateFromStorage } from "./data/worldState.js";
import { CHARACTER_CATALOG, DEFAULT_CHARACTER_ID, sanitizeCharacterId } from "../../shared/characterCatalog.js";

const PROFILE_NAME_KEY = "hene.net.playerName";
const PROFILE_CHARACTER_KEY = "hene.net.characterId";
const DEFAULT_NAME = "Player";
const CHARACTER_PREVIEW_CACHE = new Map();
const LOADING_WALKER_CACHE = new Map();
const LOAD_BAR_COLOR = "#93c5fd";
const LOADING_WALKER_COUNT = 1;
const LOADING_WALKER_SIZE = 120;
const LOADING_WALKER_FRAME_INTERVAL_MS = 40;
const TEST_LOADING_TOTAL_MS = 1800;
const BULLETIN_BOARD_TEXT_MAX = 280;

function startTestLoadingState() {
  const existing = globalThis.HENE_LOADING_STATE;
  if (existing && !existing.isComplete?.()) {
    existing.hide();
  }

  const loadingState = createLoadingState();
  globalThis.HENE_LOADING_STATE = loadingState;
  if (!loadingState) return;

  loadingState.setMessage("Loading state test...");
  const startTs = Date.now();
  const timer = setInterval(() => {
    if (!globalThis.HENE_LOADING_STATE || globalThis.HENE_LOADING_STATE !== loadingState || loadingState.isComplete?.()) {
      clearInterval(timer);
      return;
    }
    const elapsed = Date.now() - startTs;
    const ratio = Math.min(1, elapsed / TEST_LOADING_TOTAL_MS);
    loadingState.setProgress(ratio);
    if (elapsed >= TEST_LOADING_TOTAL_MS) {
      clearInterval(timer);
      loadingState.hide();
    }
  }, 60);
}

function shuffleCopy(items) {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function pickRandomCharacters(count) {
  const safeCount = Number.isFinite(count) ? Math.min(Math.max(0, Math.floor(count)), CHARACTER_CATALOG.length) : LOADING_WALKER_COUNT;
  const shuffled = shuffleCopy(CHARACTER_CATALOG);
  return shuffled.slice(0, safeCount);
}

function getLoadingWalkFramesName(frameKey) {
  return String(frameKey || "").trim();
}

function pickWalkFrameKeys(framesObj, basePrefix) {
  const keys = Object.keys(framesObj || {}).filter((k) => {
    const key = String(k || "").toLowerCase();
    return key.startsWith(`${basePrefix.toLowerCase()}_walking_`) && key.includes("_");
  });
  if (keys.length > 0) return keys;

  const idleKeys = Object.keys(framesObj || {}).filter((k) => String(k || "").toLowerCase().startsWith(`${basePrefix.toLowerCase()}_idle_`));
  return idleKeys;
}

function walkFrameCompare(a, b) {
  const aMatch = getLoadingWalkFramesName(a).match(/_(\d+)(?:\D|$)/);
  const bMatch = getLoadingWalkFramesName(b).match(/_(\d+)(?:\D|$)/);
  const aIndex = Number(aMatch?.[1] || NaN);
  const bIndex = Number(bMatch?.[1] || NaN);
  if (Number.isFinite(aIndex) && Number.isFinite(bIndex) && aIndex !== bIndex) return aIndex - bIndex;
  return getLoadingWalkFramesName(a).localeCompare(getLoadingWalkFramesName(b));
}

function drawLoadingWalkFallback(slot) {
  const ctx = slot.ctx;
  if (!ctx) return;
  ctx.clearRect(0, 0, slot.size, slot.size);
  const label = String(slot.character.label || "?").slice(0, 2);
  ctx.fillStyle = "#e5ecff";
  ctx.font = `${Math.max(11, Math.floor(slot.size * 0.28))}px monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, slot.size * 0.5, slot.size * 0.52);
}

function createLoadingWalkerGroup(container) {
  let active = true;
  const walkerWrap = document.createElement("div");
  walkerWrap.style.display = "flex";
  walkerWrap.style.gap = "4px";
  walkerWrap.style.justifyContent = "center";
  walkerWrap.style.alignItems = "center";
  walkerWrap.style.margin = "2px auto 14px";
  walkerWrap.style.minHeight = `${LOADING_WALKER_SIZE + 4}px`;
  container.appendChild(walkerWrap);

  const slots = pickRandomCharacters(LOADING_WALKER_COUNT).map((character) => {
  const slot = document.createElement("div");
  slot.style.width = `${LOADING_WALKER_SIZE}px`;
  slot.style.height = `${LOADING_WALKER_SIZE}px`;
  slot.style.position = "relative";
  slot.style.overflow = "hidden";

    const canvas = document.createElement("canvas");
    canvas.width = LOADING_WALKER_SIZE;
    canvas.height = LOADING_WALKER_SIZE;
    canvas.style.width = `${LOADING_WALKER_SIZE}px`;
    canvas.style.height = `${LOADING_WALKER_SIZE}px`;
    canvas.style.imageRendering = "auto";
    slot.appendChild(canvas);
    walkerWrap.appendChild(slot);

    return {
      character,
      canvas,
      ctx: canvas.getContext("2d"),
      size: LOADING_WALKER_SIZE,
      data: null,
      frameIndex: 0,
    };
  });

  const ticker = setInterval(() => {
    if (!active) return;
    for (const slot of slots) {
      if (!slot.data?.frames?.length) {
        drawLoadingWalkFallback(slot);
        continue;
      }
      const total = slot.data.frames.length;
      slot.frameIndex = (slot.frameIndex + 1) % total;
      const current = slot.data.frames[slot.frameIndex];
      slot.ctx.clearRect(0, 0, slot.size, slot.size);
      const frame = current?.frame;
      if (!frame || !slot.data.image) {
        drawLoadingWalkFallback(slot);
        continue;
      }
      const sx = frame.x;
      const sy = frame.y;
      const sw = frame.w;
      const sh = frame.h;
      const scale = Math.min((slot.size * 0.96) / sw, (slot.size * 0.96) / sh);
      const drawW = sw * scale;
      const drawH = sh * scale;
      const dx = Math.floor((slot.size - drawW) / 2);
      const dy = Math.floor((slot.size - drawH) / 2);
      slot.ctx.imageSmoothingEnabled = true;
      slot.ctx.imageSmoothingQuality = "high";
      slot.ctx.drawImage(slot.data.image, sx, sy, sw, sh, dx, dy, drawW, drawH);
    }
  }, LOADING_WALKER_FRAME_INTERVAL_MS);

  const onDataReady = async () => {
    if (!active) return;
    await Promise.all(
      slots.map(async (slot) => {
        if (!active) return;
        try {
          const basePrefix = slot.character.prefix;
          const atlasUrl = `${slot.character.dir}${basePrefix}_walking.json`;
          const imageUrl = `${slot.character.dir}${basePrefix}_walking.png`;
          const cacheKey = `${slot.character.dir}::${basePrefix}`;
          if (!LOADING_WALKER_CACHE.has(cacheKey)) {
            const loadTask = (async () => {
              const [atlasResponse, image] = await Promise.all([
                fetch(atlasUrl),
                new Promise((resolve, reject) => {
                  const img = new Image();
                  img.onload = () => resolve(img);
                  img.onerror = () => reject(new Error(`Failed to load ${imageUrl}`));
                  img.src = imageUrl;
                }),
              ]);
              if (!atlasResponse.ok) throw new Error(`Failed to load ${atlasUrl}`);
              const atlas = await atlasResponse.json();
              const framesObj = atlas?.frames || {};
              const candidateKeys = pickWalkFrameKeys(framesObj, basePrefix).sort(walkFrameCompare);
              const frameKeys = candidateKeys.slice();
              if (frameKeys.length === 0) return null;
              const frames = frameKeys
                .map((frameKey) => ({ frameKey, frame: framesObj[frameKey]?.frame }))
                .filter((item) => item && item.frame && Number.isFinite(item.frame.w) && Number.isFinite(item.frame.h) && item.frame.w > 0 && item.frame.h > 0)
                .map((item) => ({ frameKey: item.frameKey, frame: item.frame }));
              if (frames.length === 0) return null;
              return { image, frames };
            })();
            LOADING_WALKER_CACHE.set(cacheKey, loadTask);
          }
          const data = await LOADING_WALKER_CACHE.get(cacheKey);
          if (!data) {
            drawLoadingWalkFallback(slot);
            return;
          }
          slot.data = data;
          slot.frameIndex = 0;
          if (slot.data.frames.length > 0) {
            const first = slot.data.frames[0];
            slot.ctx.clearRect(0, 0, slot.size, slot.size);
            const frame = first.frame;
            const scale = Math.min((slot.size * 0.96) / frame.w, (slot.size * 0.96) / frame.h);
            const drawW = frame.w * scale;
            const drawH = frame.h * scale;
            const dx = Math.floor((slot.size - drawW) / 2);
            const dy = Math.floor((slot.size - drawH) / 2);
            slot.ctx.imageSmoothingEnabled = true;
            slot.ctx.imageSmoothingQuality = "high";
            slot.ctx.drawImage(slot.data.image, frame.x, frame.y, frame.w, frame.h, dx, dy, drawW, drawH);
          }
        } catch {
          slot.data = { frames: [] };
          drawLoadingWalkFallback(slot);
        }
      })
    );
  };
  onDataReady();

  return {
    destroy: () => {
      active = false;
      clearInterval(ticker);
      walkerWrap.remove();
    },
  };
}

function clampLoadingProgress(rawValue) {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function createLoadingState() {
  if (typeof document === "undefined") return null;

  const root = document.createElement("div");
  root.style.position = "fixed";
  root.style.inset = "0";
  root.style.display = "flex";
  root.style.alignItems = "center";
  root.style.justifyContent = "center";
  root.style.background = "rgba(8, 10, 18, 0.9)";
  root.style.zIndex = "99999";
  root.style.transition = "opacity 220ms ease";
  root.style.opacity = "1";

  const panel = document.createElement("div");
  panel.style.width = "min(420px, 84vw)";
  panel.style.padding = "22px";
  panel.style.background = "rgba(18, 24, 40, 0.94)";
  panel.style.border = "1px solid rgba(255,255,255,0.16)";
  panel.style.borderRadius = "12px";
  panel.style.color = "#e5ecff";
  panel.style.textAlign = "center";
  panel.style.fontFamily = "monospace";
  panel.style.boxShadow = "0 24px 48px rgba(0, 0, 0, 0.45)";

  const title = document.createElement("div");
  title.textContent = "Loading hene.town";
  title.style.fontSize = "20px";
  title.style.fontWeight = "700";
  title.style.marginBottom = "10px";

  const logoWrap = document.createElement("div");
  logoWrap.style.display = "flex";
  logoWrap.style.alignItems = "flex-end";
  logoWrap.style.justifyContent = "center";
  const walkers = createLoadingWalkerGroup(logoWrap);

  const status = document.createElement("div");
  status.textContent = "Preparing…";
  status.style.marginBottom = "14px";

  const track = document.createElement("div");
  track.style.position = "relative";
  track.style.height = "10px";
  track.style.borderRadius = "999px";
  track.style.background = "rgba(255,255,255,0.2)";
  track.style.overflow = "hidden";
  track.style.marginBottom = "10px";

  const fill = document.createElement("div");
  fill.style.position = "absolute";
  fill.style.inset = "0";
  fill.style.transformOrigin = "left";
  fill.style.transform = "scaleX(0)";
  fill.style.background = LOAD_BAR_COLOR;
  fill.style.transition = "transform 120ms linear";

  const percent = document.createElement("div");
  percent.textContent = "0%";
  percent.style.fontSize = "12px";
  percent.style.opacity = "0.9";

  track.appendChild(fill);
  panel.appendChild(title);
  panel.appendChild(logoWrap);
  panel.appendChild(status);
  panel.appendChild(track);
  panel.appendChild(percent);
  root.appendChild(panel);
  document.body.appendChild(root);

  let isComplete = false;

  const setMessage = (text) => {
    if (!status || !text) return;
    status.textContent = String(text);
  };

  const setProgress = (rawValue) => {
    if (!fill || !percent) return;
    const value = clampLoadingProgress(rawValue);
    fill.style.transform = `scaleX(${value})`;
    percent.textContent = `${Math.round(value * 100)}%`;
  };

  const hide = () => {
    if (isComplete) return;
    isComplete = true;
    setProgress(1);
    walkers?.destroy?.();
    setMessage("Ready");
    root.style.opacity = "0";
    setTimeout(() => {
      if (root.parentNode) root.remove();
      if (globalThis.HENE_LOADING_STATE === controller) {
        globalThis.HENE_LOADING_STATE = null;
      }
    }, 240);
  };

  const controller = {
    setMessage,
    setProgress,
    hide,
    isComplete: () => isComplete,
  };

  return controller;
}

function sanitizeName(raw) {
  if (typeof raw !== "string") return DEFAULT_NAME;
  const name = raw.trim().slice(0, 24);
  return name || DEFAULT_NAME;
}

function sanitizeBulletinText(raw) {
  if (typeof raw !== "string") return "";
  let text = raw.replace(/\r\n?/g, "\n");
  text = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, "");
  text = text.replace(/[\u200B-\u200D\u2060\uFEFF]/g, "");
  text = text.trim();
  if (text.length > BULLETIN_BOARD_TEXT_MAX) text = text.slice(0, BULLETIN_BOARD_TEXT_MAX).trim();
  return text;
}

function sanitizeCharacter(raw) {
  return sanitizeCharacterId(raw);
}

function readProfile() {
  try {
    const name = sanitizeName(localStorage.getItem(PROFILE_NAME_KEY));
    const characterId = sanitizeCharacter(localStorage.getItem(PROFILE_CHARACTER_KEY));
    const hasName = typeof localStorage.getItem(PROFILE_NAME_KEY) === "string";
    const hasCharacter = typeof localStorage.getItem(PROFILE_CHARACTER_KEY) === "string";
    return { name, characterId, isComplete: hasName && hasCharacter };
  } catch {
    return { name: DEFAULT_NAME, characterId: DEFAULT_CHARACTER_ID, isComplete: false };
  }
}

function saveProfile(name, characterId) {
  try {
    localStorage.setItem(PROFILE_NAME_KEY, sanitizeName(name));
    localStorage.setItem(PROFILE_CHARACTER_KEY, sanitizeCharacter(characterId));
  } catch {
    // no-op
  }
}

async function loadCharacterIdlePreview(prefix, dir) {
  const cacheKey = `${dir}${prefix}`;
  if (!CHARACTER_PREVIEW_CACHE.has(cacheKey)) {
    const job = (async () => {
      const atlasUrl = `${dir}${prefix}_idle.json`;
      const imageUrl = `${dir}${prefix}_idle.png`;
      const [atlasResponse, image] = await Promise.all([
        fetch(atlasUrl),
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error(`Failed to load ${imageUrl}`));
          img.src = imageUrl;
        }),
      ]);
      if (!atlasResponse.ok) throw new Error(`Failed to load ${atlasUrl}`);
      const atlas = await atlasResponse.json();
      const frames = atlas?.frames || {};
      const wantedKey = `${prefix}_idle_00`;
      const frameEntry = frames[wantedKey] || frames[Object.keys(frames)[0]];
      const frame = frameEntry?.frame;
      if (!frame || !Number.isFinite(frame.w) || !Number.isFinite(frame.h)) {
        throw new Error(`Missing idle frame in ${atlasUrl}`);
      }
      const sampleCanvas = document.createElement("canvas");
      sampleCanvas.width = frame.w;
      sampleCanvas.height = frame.h;
      const sampleCtx = sampleCanvas.getContext("2d", { willReadFrequently: true });
      let opaqueBounds = null;
      if (sampleCtx) {
        sampleCtx.clearRect(0, 0, frame.w, frame.h);
        sampleCtx.drawImage(
          image,
          frame.x,
          frame.y,
          frame.w,
          frame.h,
          0,
          0,
          frame.w,
          frame.h,
        );
        try {
          const data = sampleCtx.getImageData(0, 0, frame.w, frame.h).data;
          let minX = frame.w;
          let minY = frame.h;
          let maxX = -1;
          let maxY = -1;
          const alphaThreshold = 8;
          for (let y = 0; y < frame.h; y++) {
            const rowStart = y * frame.w * 4;
            for (let x = 0; x < frame.w; x++) {
              if (data[rowStart + x * 4 + 3] > alphaThreshold) {
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
              }
            }
          }
          if (maxX >= minX && maxY >= minY) {
            opaqueBounds = {
              x: minX,
              y: minY,
              w: maxX - minX + 1,
              h: maxY - minY + 1,
            };
          }
        } catch {
          opaqueBounds = null;
        }
      }
      return {
        image,
        frame: { x: frame.x, y: frame.y, w: frame.w, h: frame.h },
        opaqueBounds,
      };
    })();
    CHARACTER_PREVIEW_CACHE.set(cacheKey, job);
  }
  return CHARACTER_PREVIEW_CACHE.get(cacheKey);
}

function openProfileModal(profile) {
  return new Promise((resolve) => {
    const canDismiss = !!profile?.isComplete;
    globalThis.HENE_UI_MODAL_OPEN = true;
    const root = document.createElement("div");
    root.style.position = "fixed";
    root.style.inset = "0";
    root.style.background = "rgba(10, 12, 20, 0.78)";
    root.style.display = "flex";
    root.style.alignItems = "center";
    root.style.justifyContent = "center";
    root.style.zIndex = "9999";

    const panel = document.createElement("div");
    panel.style.width = "min(760px, 94vw)";
    panel.style.background = "rgba(26, 30, 43, 0.96)";
    panel.style.border = "1px solid rgba(255,255,255,0.15)";
    panel.style.borderRadius = "12px";
    panel.style.padding = "18px";
    panel.style.maxHeight = "94vh";
    panel.style.overflow = "hidden";
    panel.style.color = "white";
    panel.style.fontFamily = "monospace";
    panel.style.boxShadow = "0 18px 48px rgba(0,0,0,0.35)";

    const header = document.createElement("div");
    header.style.display = "flex";
    header.style.alignItems = "center";
    header.style.justifyContent = "space-between";
    header.style.marginBottom = "12px";

    const title = document.createElement("div");
    title.textContent = "Enter Your Player Profile";
    title.style.fontSize = "18px";
    title.style.fontWeight = "700";

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.textContent = "✕";
    closeButton.title = "Close";
    closeButton.setAttribute("aria-label", "Close");
    closeButton.style.width = "28px";
    closeButton.style.height = "28px";
    closeButton.style.borderRadius = "6px";
    closeButton.style.border = "1px solid rgba(255,255,255,0.25)";
    closeButton.style.background = "rgba(255,255,255,0.08)";
    closeButton.style.color = "white";
    closeButton.style.cursor = canDismiss ? "pointer" : "not-allowed";
    closeButton.style.opacity = canDismiss ? "1" : "0.45";

    const nameLabel = document.createElement("label");
    nameLabel.textContent = "Name";
    nameLabel.style.display = "block";
    nameLabel.style.marginBottom = "6px";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.maxLength = 24;
    nameInput.value = profile.name || DEFAULT_NAME;
    nameInput.placeholder = "Player name";
    nameInput.style.width = "100%";
    nameInput.style.padding = "10px 12px";
    nameInput.style.marginBottom = "12px";
    nameInput.style.borderRadius = "8px";
    nameInput.style.border = "1px solid rgba(255,255,255,0.25)";
    nameInput.style.background = "rgba(255,255,255,0.08)";
    nameInput.style.color = "white";

    const characterLabel = document.createElement("label");
    characterLabel.textContent = "Character";
    characterLabel.style.display = "block";
    characterLabel.style.marginBottom = "6px";

    let selectedCharacterId = sanitizeCharacter(profile.characterId || DEFAULT_CHARACTER_ID);
    const characterGrid = document.createElement("div");
    characterGrid.style.display = "grid";
    characterGrid.style.gridTemplateColumns = "repeat(4, minmax(0, 1fr))";
    characterGrid.style.gap = "10px";
    characterGrid.style.maxHeight = "440px";
    characterGrid.style.overflowY = "auto";
    characterGrid.style.padding = "6px";
    characterGrid.style.border = "1px solid rgba(255,255,255,0.15)";
    characterGrid.style.borderRadius = "8px";
    characterGrid.style.background = "rgba(255,255,255,0.04)";
    characterGrid.style.marginBottom = "14px";

    const submit = document.createElement("button");
    submit.type = "button";
    submit.textContent = "Save";
    submit.style.width = "100%";
    submit.style.padding = "10px 12px";
    submit.style.borderRadius = "8px";
    submit.style.border = "none";
    submit.style.background = "#3b82f6";
    submit.style.color = "white";
    submit.style.fontWeight = "700";
    submit.style.cursor = "pointer";

    const close = () => {
      globalThis.HENE_UI_MODAL_OPEN = false;
      root.remove();
    };

    const dismiss = () => {
      if (!canDismiss) return;
      close();
      resolve(null);
    };

    const complete = () => {
      const name = sanitizeName(nameInput.value);
      const characterId = sanitizeCharacter(selectedCharacterId);
      saveProfile(name, characterId);
      close();
      resolve({ name, characterId, isComplete: true });
    };

    const tileButtons = [];
    const updateCharacterSelectionUI = () => {
      for (const tile of tileButtons) {
        const isSelected = tile.dataset.characterId === selectedCharacterId;
        tile.style.border = isSelected
          ? "2px solid rgba(59,130,246,1)"
          : "1px solid rgba(255,255,255,0.2)";
        tile.style.background = isSelected
          ? "rgba(59,130,246,0.2)"
          : "rgba(255,255,255,0.06)";
        const marker = tile.querySelector("[data-checkmark]");
        if (marker) marker.style.opacity = isSelected ? "1" : "0";
      }
    };

    for (const character of CHARACTER_CATALOG) {
      const tile = document.createElement("button");
      tile.type = "button";
      tile.dataset.characterId = character.id;
      tile.style.position = "relative";
      tile.style.borderRadius = "8px";
      tile.style.border = "1px solid rgba(255,255,255,0.2)";
      tile.style.background = "rgba(255,255,255,0.06)";
      tile.style.color = "white";
      tile.style.cursor = "pointer";
      tile.style.padding = "8px";
      tile.style.display = "flex";
      tile.style.flexDirection = "column";
      tile.style.alignItems = "center";
      tile.style.gap = "6px";
      tile.style.minHeight = "112px";

      const checkmark = document.createElement("div");
      checkmark.dataset.checkmark = "true";
      checkmark.textContent = "✓";
      checkmark.style.position = "absolute";
      checkmark.style.top = "5px";
      checkmark.style.right = "6px";
      checkmark.style.fontSize = "11px";
      checkmark.style.fontWeight = "700";
      checkmark.style.color = "#93c5fd";
      checkmark.style.opacity = "0";

      const imgWrap = document.createElement("div");
      imgWrap.style.width = "100%";
      imgWrap.style.aspectRatio = "1 / 1";
      imgWrap.style.borderRadius = "6px";
      imgWrap.style.display = "flex";
      imgWrap.style.alignItems = "center";
      imgWrap.style.justifyContent = "center";
      imgWrap.style.background = "rgba(0,0,0,0.2)";
      imgWrap.style.overflow = "hidden";

      const spriteCanvas = document.createElement("canvas");
      spriteCanvas.width = 256;
      spriteCanvas.height = 256;
      spriteCanvas.style.width = "100%";
      spriteCanvas.style.height = "100%";
      spriteCanvas.style.imageRendering = "auto";

      loadCharacterIdlePreview(character.prefix, character.dir)
        .then(({ image, frame, opaqueBounds }) => {
          const ctx = spriteCanvas.getContext("2d");
          if (!ctx) throw new Error("Canvas context unavailable");
          ctx.clearRect(0, 0, spriteCanvas.width, spriteCanvas.height);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          const crop = opaqueBounds && opaqueBounds.w > 0 && opaqueBounds.h > 0
            ? {
                x: frame.x + opaqueBounds.x,
                y: frame.y + opaqueBounds.y,
                w: opaqueBounds.w,
                h: opaqueBounds.h,
              }
            : frame;
          const previewPad = 6;
          const scale = Math.min(
            (spriteCanvas.width - previewPad * 2) / crop.w,
            (spriteCanvas.height - previewPad * 2) / crop.h,
          );
          const drawW = Math.round(crop.w * scale);
          const drawH = Math.round(crop.h * scale);
          const dx = Math.floor((spriteCanvas.width - drawW) / 2);
          const dy = Math.floor((spriteCanvas.height - drawH) / 2);
          ctx.drawImage(
            image,
            crop.x,
            crop.y,
            crop.w,
            crop.h,
            dx,
            dy,
            drawW,
            drawH,
          );
        })
        .catch(() => {
          spriteCanvas.style.display = "none";
          imgWrap.textContent = "?";
          imgWrap.style.color = "rgba(255,255,255,0.7)";
          imgWrap.style.fontWeight = "700";
        });

      const label = document.createElement("div");
      label.textContent = character.label;
      label.style.fontSize = "11px";
      label.style.textAlign = "center";
      label.style.lineHeight = "1.2";
      label.style.opacity = "0.92";

      imgWrap.appendChild(spriteCanvas);
      tile.appendChild(checkmark);
      tile.appendChild(imgWrap);
      tile.appendChild(label);
      tile.addEventListener("click", () => {
        selectedCharacterId = character.id;
        updateCharacterSelectionUI();
      });
      tileButtons.push(tile);
      characterGrid.appendChild(tile);
    }
    updateCharacterSelectionUI();

    submit.addEventListener("click", complete);
    closeButton.addEventListener("click", dismiss);
    nameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") complete();
    });
    const stopKeyToGame = (e) => e.stopPropagation();
    nameInput.addEventListener("keydown", stopKeyToGame, true);
    characterGrid.addEventListener("keydown", stopKeyToGame, true);

    root.addEventListener("click", (e) => {
      if (e.target === root) dismiss();
    });
    panel.addEventListener("click", (e) => e.stopPropagation());

    header.appendChild(title);
    header.appendChild(closeButton);
    panel.appendChild(header);
    panel.appendChild(nameLabel);
    panel.appendChild(nameInput);
    panel.appendChild(characterLabel);
    panel.appendChild(characterGrid);
    panel.appendChild(submit);
    root.appendChild(panel);
    document.body.appendChild(root);
    nameInput.focus();
    nameInput.select();
  });
}

function openQuestBoardModal({
  localPlayerId = null,
  messages = [],
  onCreate = () => {},
  onUpdate = () => {},
  onDelete = () => {},
  onClose = () => {},
} = {}) {
  let currentLocalPlayerId = localPlayerId == null ? null : String(localPlayerId);
  let currentMessages = Array.isArray(messages) ? messages : [];
  let editingMessageId = null;
  let closed = false;

  const root = document.createElement("div");
  root.style.position = "fixed";
  root.style.inset = "0";
  root.style.background = "rgba(10, 12, 20, 0.78)";
  root.style.display = "flex";
  root.style.alignItems = "center";
  root.style.justifyContent = "center";
  root.style.zIndex = "10000";
  root.style.padding = "16px";

  const panel = document.createElement("div");
  panel.style.width = "min(780px, 96vw)";
  panel.style.height = "min(860px, 92vh)";
  panel.style.display = "flex";
  panel.style.flexDirection = "column";
  panel.style.background = "rgba(22, 27, 38, 0.96)";
  panel.style.border = "1px solid rgba(255,255,255,0.18)";
  panel.style.borderRadius = "12px";
  panel.style.padding = "14px";
  panel.style.color = "white";
  panel.style.fontFamily = "monospace";
  panel.style.boxShadow = "0 18px 48px rgba(0,0,0,0.35)";

  const header = document.createElement("div");
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "space-between";
  header.style.gap = "12px";
  header.style.marginBottom = "10px";

  const title = document.createElement("div");
  title.textContent = "Message Board";
  title.style.fontSize = "18px";
  title.style.fontWeight = "700";

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.textContent = "✕";
  closeButton.title = "Close";
  closeButton.setAttribute("aria-label", "Close");
  closeButton.style.width = "28px";
  closeButton.style.height = "28px";
  closeButton.style.borderRadius = "6px";
  closeButton.style.border = "1px solid rgba(255,255,255,0.25)";
  closeButton.style.background = "rgba(255,255,255,0.08)";
  closeButton.style.color = "white";
  closeButton.style.cursor = "pointer";

  const subtitle = document.createElement("div");
  subtitle.textContent = "Leave a message for everyone in this room.";
  subtitle.style.fontSize = "12px";
  subtitle.style.color = "rgba(210,220,245,0.82)";
  subtitle.style.marginBottom = "10px";

  const list = document.createElement("div");
  list.style.flex = "1";
  list.style.minHeight = "220px";
  list.style.maxHeight = "48vh";
  list.style.overflowY = "auto";
  list.style.border = "1px solid rgba(255,255,255,0.12)";
  list.style.borderRadius = "10px";
  list.style.padding = "8px";
  list.style.background = "rgba(7, 10, 16, 0.45)";

  const composer = document.createElement("div");
  composer.style.marginTop = "12px";
  composer.style.display = "flex";
  composer.style.flexDirection = "column";
  composer.style.gap = "8px";

  const composerLabel = document.createElement("div");
  composerLabel.textContent = "New message";
  composerLabel.style.fontSize = "12px";
  composerLabel.style.color = "rgba(210,220,245,0.86)";

  const textarea = document.createElement("textarea");
  textarea.maxLength = BULLETIN_BOARD_TEXT_MAX;
  textarea.rows = 4;
  textarea.placeholder = "Write a message...";
  textarea.style.width = "100%";
  textarea.style.resize = "none";
  textarea.style.padding = "10px";
  textarea.style.borderRadius = "8px";
  textarea.style.border = "1px solid rgba(255,255,255,0.2)";
  textarea.style.background = "rgba(255,255,255,0.06)";
  textarea.style.color = "white";
  textarea.style.fontFamily = "monospace";
  textarea.style.fontSize = "12px";
  textarea.style.height = "92px";

  const composerFooter = document.createElement("div");
  composerFooter.style.display = "flex";
  composerFooter.style.alignItems = "center";
  composerFooter.style.justifyContent = "space-between";
  composerFooter.style.gap = "8px";

  const helperText = document.createElement("div");
  helperText.style.fontSize = "11px";
  helperText.style.opacity = "0.72";
  helperText.textContent = `0/${BULLETIN_BOARD_TEXT_MAX}`;

  const actionWrap = document.createElement("div");
  actionWrap.style.display = "flex";
  actionWrap.style.gap = "8px";

  const cancelEdit = document.createElement("button");
  cancelEdit.type = "button";
  cancelEdit.textContent = "Cancel Edit";
  cancelEdit.style.padding = "8px 10px";
  cancelEdit.style.borderRadius = "8px";
  cancelEdit.style.border = "1px solid rgba(255,255,255,0.2)";
  cancelEdit.style.background = "rgba(255,255,255,0.08)";
  cancelEdit.style.color = "white";
  cancelEdit.style.cursor = "pointer";
  cancelEdit.style.display = "none";

  const submit = document.createElement("button");
  submit.type = "button";
  submit.textContent = "Post";
  submit.style.padding = "8px 12px";
  submit.style.borderRadius = "8px";
  submit.style.border = "none";
  submit.style.background = "#3b82f6";
  submit.style.color = "white";
  submit.style.fontWeight = "700";
  submit.style.cursor = "pointer";

  const stopKeyToGame = (event) => event.stopPropagation();
  root.addEventListener("keydown", stopKeyToGame, true);
  panel.addEventListener("click", (event) => event.stopPropagation());
  root.addEventListener("click", (event) => {
    if (event.target === root) close();
  });
  closeButton.addEventListener("click", () => close());

  const setEditing = (messageId = null, text = "") => {
    editingMessageId = messageId;
    const isEditing = !!editingMessageId;
    composerLabel.textContent = isEditing ? "Edit message" : "New message";
    submit.textContent = isEditing ? "Save" : "Post";
    cancelEdit.style.display = isEditing ? "inline-flex" : "none";
    textarea.value = typeof text === "string" ? text : "";
    helperText.textContent = `${textarea.value.length}/${BULLETIN_BOARD_TEXT_MAX}`;
    if (isEditing) textarea.focus();
  };

  const updateHelperText = () => {
    const clean = sanitizeBulletinText(textarea.value);
    helperText.textContent = `${clean.length}/${BULLETIN_BOARD_TEXT_MAX}`;
  };

  const formatTime = (value) => {
    const ts = Number(value);
    if (!Number.isFinite(ts)) return "";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "";
    }
  };

  const renderMessages = () => {
    list.innerHTML = "";
    const rows = (Array.isArray(currentMessages) ? currentMessages : []).slice().sort((a, b) => {
      const aTime = Number.isFinite(Number(a?.createdAt)) ? Number(a.createdAt) : 0;
      const bTime = Number.isFinite(Number(b?.createdAt)) ? Number(b.createdAt) : 0;
      if (aTime !== bTime) return bTime - aTime;
      return String(b?.id || "").localeCompare(String(a?.id || ""));
    });
    if (rows.length === 0) {
      const empty = document.createElement("div");
      empty.textContent = "No messages yet. Be the first to post.";
      empty.style.fontSize = "12px";
      empty.style.opacity = "0.8";
      empty.style.padding = "8px";
      list.appendChild(empty);
      return;
    }
    for (const entry of rows) {
      const messageId = String(entry?.id || "").trim().toLowerCase();
      if (!messageId) continue;
      const ownerId = String(entry?.playerId || "");
      const isMine = !!currentLocalPlayerId && ownerId === currentLocalPlayerId;
      const card = document.createElement("div");
      card.style.border = "1px solid rgba(255,255,255,0.12)";
      card.style.borderRadius = "8px";
      card.style.padding = "8px";
      card.style.background = isMine ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.04)";
      card.style.marginBottom = "8px";

      const cardHeader = document.createElement("div");
      cardHeader.style.display = "flex";
      cardHeader.style.alignItems = "center";
      cardHeader.style.justifyContent = "space-between";
      cardHeader.style.gap = "8px";
      cardHeader.style.marginBottom = "6px";

      const author = document.createElement("div");
      author.textContent = `${String(entry?.name || "Player")}${isMine ? " (You)" : ""}`;
      author.style.fontSize = "12px";
      author.style.fontWeight = "700";

      const time = document.createElement("div");
      const updatedAt = Number.isFinite(Number(entry?.updatedAt)) ? Number(entry.updatedAt) : null;
      const createdAt = Number.isFinite(Number(entry?.createdAt)) ? Number(entry.createdAt) : null;
      time.textContent = formatTime(updatedAt || createdAt || Date.now());
      time.style.fontSize = "10px";
      time.style.opacity = "0.7";

      const body = document.createElement("div");
      body.textContent = String(entry?.text || "");
      body.style.whiteSpace = "pre-wrap";
      body.style.wordBreak = "break-word";
      body.style.fontSize = "12px";
      body.style.lineHeight = "1.35";

      cardHeader.appendChild(author);
      cardHeader.appendChild(time);
      card.appendChild(cardHeader);
      card.appendChild(body);

      if (isMine) {
        const actions = document.createElement("div");
        actions.style.display = "flex";
        actions.style.justifyContent = "flex-end";
        actions.style.gap = "8px";
        actions.style.marginTop = "8px";

        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.textContent = "Edit";
        editBtn.style.padding = "5px 8px";
        editBtn.style.borderRadius = "6px";
        editBtn.style.border = "1px solid rgba(255,255,255,0.2)";
        editBtn.style.background = "rgba(255,255,255,0.08)";
        editBtn.style.color = "white";
        editBtn.style.cursor = "pointer";
        editBtn.addEventListener("click", () => setEditing(messageId, String(entry?.text || "")));

        const deleteBtn = document.createElement("button");
        deleteBtn.type = "button";
        deleteBtn.textContent = "Delete";
        deleteBtn.style.padding = "5px 8px";
        deleteBtn.style.borderRadius = "6px";
        deleteBtn.style.border = "1px solid rgba(244,63,94,0.4)";
        deleteBtn.style.background = "rgba(244,63,94,0.15)";
        deleteBtn.style.color = "white";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.addEventListener("click", () => onDelete(messageId));

        actions.appendChild(editBtn);
        actions.appendChild(deleteBtn);
        card.appendChild(actions);
      }

      list.appendChild(card);
    }
  };

  const submitMessage = () => {
    const text = sanitizeBulletinText(textarea.value);
    if (!text) return;
    if (editingMessageId) {
      onUpdate(editingMessageId, text);
    } else {
      onCreate(text);
    }
    setEditing(null, "");
    updateHelperText();
  };

  const close = () => {
    if (closed) return;
    closed = true;
    globalThis.HENE_UI_MODAL_OPEN = false;
    root.remove();
    onClose();
  };

  submit.addEventListener("click", submitMessage);
  cancelEdit.addEventListener("click", () => setEditing(null, ""));
  textarea.addEventListener("input", updateHelperText);
  textarea.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      submitMessage();
    }
  });

  actionWrap.appendChild(cancelEdit);
  actionWrap.appendChild(submit);
  composerFooter.appendChild(helperText);
  composerFooter.appendChild(actionWrap);
  composer.appendChild(composerLabel);
  composer.appendChild(textarea);
  composer.appendChild(composerFooter);

  header.appendChild(title);
  header.appendChild(closeButton);
  panel.appendChild(header);
  panel.appendChild(subtitle);
  panel.appendChild(list);
  panel.appendChild(composer);
  root.appendChild(panel);
  document.body.appendChild(root);
  globalThis.HENE_UI_MODAL_OPEN = true;
  renderMessages();
  textarea.focus();

  return {
    update({ messages: nextMessages = [], localPlayerId: nextLocalPlayerId = null } = {}) {
      if (closed) return;
      currentMessages = Array.isArray(nextMessages) ? nextMessages : [];
      currentLocalPlayerId = nextLocalPlayerId == null ? null : String(nextLocalPlayerId);
      renderMessages();
    },
    close,
  };
}

function ensureProfile() {
  const profile = readProfile();
  if (profile.isComplete) return Promise.resolve(profile);
  return openProfileModal(profile);
}

function startGame() {
  loadWorldStateFromStorage();
  globalThis.clearSavedResume = clearSavedResume;
  const loadingState = createLoadingState();
  globalThis.HENE_LOADING_STATE = loadingState;

  const devicePixelRatio = Number(globalThis?.devicePixelRatio) || 1;
  const renderResolution = Math.max(1, Math.min(1.5, devicePixelRatio));

  const config = {
    type: Phaser.AUTO,
    parent: "game-container",
    width: 1600,
    height: 900,
    pixelArt: false,
    antialias: true,
    resolution: renderResolution,
    backgroundColor: "#111",
    physics: {
      default: "arcade",
      arcade: {
        gravity: { y: 0 },
        debug: false,
      },
    },
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      autoRound: true,
    },
    scene: [VillageScene, MapCombat01Scene],
  };

  new Phaser.Game(config);
}

ensureProfile().then((profile) => {
  if (profile?.name) globalThis.HENE_PLAYER_NAME = profile.name;
  if (profile?.characterId) globalThis.HENE_CHARACTER_ID = profile.characterId;
  globalThis.openPlayerProfileModal = async () => {
    const next = await openProfileModal(readProfile());
    if (next?.name) globalThis.HENE_PLAYER_NAME = next.name;
    if (next?.characterId) globalThis.HENE_CHARACTER_ID = next.characterId;
    return next;
  };
  globalThis.openQuestBoardModal = (options = {}) => openQuestBoardModal(options);
  globalThis.startTestLoadingState = startTestLoadingState;
  startGame();
});
